import { DataTable, DataTablePagination } from "@/src/components/ui/data-table";
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import { useFilters, usePageSize, usePageSort, useSearch } from "../state";
import { useNavigate } from "react-router-dom";
import { CustomerType, PayerType } from "../model";
import { fetch } from "@/src/lib/fetch";
import { getCustomersDataColumns } from "./customersColumns";

type CustomerData = {
  data: CustomerType[];
} & DataTablePagination;

export function CustomersTable({
  shouldRefetch,
  setShouldRefetch,
}: {
  shouldRefetch: boolean;
  setShouldRefetch: Dispatch<SetStateAction<boolean>>;
}) {
  const [customers, setCustomers] = useState<CustomerData>({
    data: [],
    total: 1,
    pagination: { page: 1, pageSize: 1 },
  });
  const [page, setPage] = useState(1);

  const navigate = useNavigate();

  const search = useSearch();
  const pageSize = usePageSize();
  const { customerSortBy, sortBy } = usePageSort();
  const { customerName, customerEmail } = useFilters();

  // Construct the query string with all filters and pagination
  const queryParams = new URLSearchParams({
    page: page.toString(),
    limit: pageSize.toString(),
    orderBy: sortBy,
    payerType: PayerType.CUSTOMER,
  });

  // Append optional filters if available
  if (customerName) queryParams.append("customerName", customerName.toString());
  if (customerEmail) queryParams.append("customerEmail", customerEmail.toString());

  // Final query string
  const queryString = queryParams.toString();

  const fetchCustomers = async () => {
    try {
      return await fetch<{ data: CustomerType[] } & DataTablePagination>(
        `/api/users/paginated?${queryString}&keyword=${""}`,
        "GET",
        undefined,
        undefined,
      );
    } catch (error) {
      console.error("Error fetching customers", error);
    }
  };

  useEffect(() => {
    if (page > 1) {
      setPage(1);
    } else {
      fetchCustomers().then((data) => {
        if (data) {
          setCustomers(data);
        } else {
          setCustomers({
            data: [],
            total: 0,
            pagination: { page: 1, pageSize: 10 },
          });
        }
      });
    }
  }, [search, pageSize, sortBy, customerSortBy, customerName, customerEmail, shouldRefetch]);

  useEffect(() => {
    fetchCustomers().then((data) => {
      if (data) {
        setCustomers(data);
      } else {
        setCustomers({
          data: [],
          total: 0,
          pagination: { page: 1, pageSize: 10 },
        });
      }
    });
  }, [page]);

  const columns = useMemo(() => getCustomersDataColumns(setShouldRefetch), [shouldRefetch]);
  return (
    <div className="rounded-md bg-white border">
      <DataTable
        columns={columns}
        data={customers?.data}
        pagination={customers}
        onPageClick={(page) => setPage(page)}
        onRowClick={(r) => navigate(`/customers`)}
      />
    </div>
  );
}
